/**
 * What a conversation is made of, and the two lists every part of it carries.
 *
 * A line is something said, with the conditions under which it is the one said,
 * the answers that can be given to it, and what reaching it does. A branch says
 * nothing and picks one of two ways. The shape they make is drawn on the canvas
 * next door; this file is the shapes themselves and the two editors that every
 * part of one needs: what has to be true, and what happens.
 */

import { lookAtTile, pickTile } from './tile-pick';
import { stageAt, stageOf } from './quest-stage';
import type { ItemDefinitionWire, QuestWire } from '@inverse/protocol';
import { SKILL_KEYS } from '@inverse/common';
import { DEEDS } from './deeds';
import { Written } from './Written';
import { SoundPicker } from './Sounds';
import { ItemPicker } from './ItemPicker';

/** What the tools hold while a conversation is being written. */
export interface DialogueLine {
  id: string;
  /** What sort of node it is: said, asked, offered, or where things begin. */
  kind?: 'line' | 'branch' | 'directory' | 'start' | 'priority' | 'note' | 'checkpoint';
  text: string;
  when: Condition[];
  does: Effect[];
  /** Where a branch goes when its conditions hold, and where it goes when they do not. */
  pass?: string;
  fail?: string;
  /** The one way on, for anything with exactly one. */
  next?: string;
  /** Said once and never again. */
  once?: boolean;
  /** A checkpoint that ends the talk the first time it is reached. */
  stops?: boolean;
  /** Which priority is looked at first, highest before lowest. */
  weight?: number;
  /** Where it sits on the canvas, so a conversation keeps the shape it was given. */
  at?: { x: number; y: number };
  options: {
    text: string;
    goto: string;
    when: Condition[];
    does: Effect[];
    /** Hidden or shown greyed when its conditions do not hold. */
    ifUnmet?: 'hide' | 'disable';
  }[];
}

export interface Condition {
  /**
   * A flag the world holds rather than a player.
   *
   * What has happened, as against what somebody has done.
   */
  worldFlag?: string;
  worldIs?: string;
  /** The stretch of day it has to be: dawn, day, dusk or night. */
  atTime?: string;
  /** Something they have to be wearing, which is not the same as carrying it. */
  wearing?: number;
  /** Whether that means the belt rather than a gear slot. */
  onBelt?: boolean;
  /** Somewhere they have to be standing. */
  inArea?: { tx: number; tz: number; width: number; height: number; plane: number };
  /**
   * Somewhere named that they have to be standing in.
   *
   * By the place rather than by its corners, so a quest that says "in the town"
   * still means the town after somebody moves a wall.
   */
  inPlace?: number;
  /** A lasting effect they are under: warded, cursed, poisoned. */
  status?: string;
  /** A prayer they have to be holding up at the moment. */
  praying?: number;
  flag?: string;
  is?: string;
  quest?: string;
  atLeast?: number;
  atMost?: number;
  /**
   * That the quest named is finished, however many steps it turns out to have.
   *
   * Asked this way rather than as a step number, because a step number goes
   * stale the moment somebody adds a step to the quest it counts.
   */
  finished?: boolean;
  skill?: string;
  level?: number;
  combat?: number;
  /** A kind of character, and how many of them they have killed. */
  killed?: number;
  atLeastKilled?: number;
  item?: number;
  count?: number;
  /** Something they have to have done, named by the skill it took. */
  deed?: string;
  deedOf?: number;
  atLeastDone?: number;
  /** Something standing somewhere: how one thing asks about another. */
  standing?: { tx: number; tz: number; plane: number; objectId: number };
  /** How often this holds at all, as a percentage. */
  chance?: number;
  not?: boolean;
  /**
   * How this one joins what came before: false is AND, true is OR.
   *
   * On the first condition of a bracket the word is about the whole bracket
   * rather than about the condition, which is how brackets are joined without a
   * second control for it.
   */
  or?: boolean;
  /** Which bracket it sits in. Everything is in nought until somebody says so. */
  group?: number;
}

export interface Effect {
  /** Whether a hand-over happens once for a player and never again. */
  once?: boolean;
  /** A shop this opens, by its number. */
  opensShop?: number;
  /** A window opened by saying something, and what it costs to open it. */
  opensMenu?: string;
  fee?: number;
  /** Somewhere to put them. */
  teleport?: { tx: number; tz: number; plane: number };
  /** Somebody who walks into the scene for this player alone. */
  summon?: {
    npcId: number;
    tx: number;
    tz: number;
    plane: number;
    walkTx?: number;
    walkTz?: number;
    forTicks: number;
  };
  dismiss?: boolean;
  /** Holds the camera on a spot for a moment. */
  holdCamera?: { tx: number; tz: number; tenths: number };
  /** What happens to a handover a full bag cannot take. */
  whenFull?: 'drop' | 'bank' | 'held';
  fullSays?: string;
  setFlag?: string;
  /** A flag written against the world rather than against whoever did it. */
  setsWorld?: string;
  value?: string;
  quest?: string;
  step?: number;
  give?: number;
  count?: number;
  take?: number;
  skill?: string;
  experience?: number;
  says?: string;

  // What a thing in the world does, as opposed to what a person says.
  /** Hit points back, and run energy back. */
  heals?: number;
  restores?: number;
  /** Mana and faith back, on the same terms. */
  restoresMana?: number;
  restoresFaith?: number;
  /** A sound from the library, heard by whoever is close. */
  sound?: string;
  /** A line in their own voice rather than the world's. */
  thinks?: string;
  /** An animation on whoever did this. */
  plays?: 'bend' | 'pickup';
  /** The thing itself turns into another, for a while or for good. */
  becomes?: { objectId: number; forTicks: number; forWhom?: 'world' | 'me' };
  /** Something at a named tile turns into another: a lever's gate. */
  changes?: {
    tx: number;
    tz: number;
    plane: number;
    objectId: number;
    forTicks: number;
    /** Whether it changes for the world or only for whoever did it. */
    forWhom?: 'world' | 'me';
  };
  /** Health off whoever set this off: a trap, a brazier, a thorn hedge. */
  hurts?: { amount: number; canKill: boolean };
  opensBank?: boolean;
  /** Opens the exchange, where offers to buy and sell are left standing. */
  opensExchange?: boolean;
  /** One of several things, by weight: a casket opened, a crate searched. */
  rolls?: {
    entries: { itemId: number; min: number; max: number; weight: number }[];
    times: number;
    /** Ticks before it fills again. Nought is emptied for good; absent is never empty. */
    emptyFor?: number;
    /** Whether being emptied is about one person or about the world. */
    each?: boolean;
    emptySays?: string;
  };
  /** Whatever is made at a named station. */
  opensCrafting?: number;
  /** Into a copy of somewhere, or back out of one. */
  travels?: { region: string; instanced: boolean };
  /** Work at a place: chopping, mining, netting. */
  gathers?: {
    skill: string;
    level: number;
    experience: number;
    itemId: number;
    catches: { itemId: number; weight: number; level: number; experience: number }[];
    animation: 'none' | 'bend';
    tools: number[];
    interval: number;
    chance: number;
    chancePerLevel: number;
    depletesTo: number;
    depleteTicks: number;
  };
}

// Every skill, from the one list the game keeps. A condition or a reward that
// could only name six of them was a condition that could not say most of what
// the game already knows how to be.
const SKILLS = SKILL_KEYS;

/** The list of things that have to be true. */
export function Conditions({
  held,
  items,
  quests,
  characters,
  objects = [],
  onChange,
}: {
  held: readonly Condition[];
  items: readonly ItemDefinitionWire[];
  quests: readonly QuestWire[];
  /** For naming what somebody has to have killed, rather than asking for its id. */
  characters?: readonly { id: number; name: string }[];
  /** For naming the scenery a deed was done to, rather than asking for its id. */
  objects?: readonly { id: number; name: string }[];
  onChange: (conditions: Condition[]) => void;
}): JSX.Element {
  const change = (at: number, patch: Condition): void => {
    onChange(held.map((entry, index) => (index === at ? { ...entry, ...patch } : entry)));
  };

  // The brackets, in the order they were written rather than by number, which
  // is the order the world works them out in and the order they are read in.
  const brackets: { group: number; at: number[] }[] = [];
  for (const [at, condition] of held.entries()) {
    const group = condition.group ?? 0;
    const already = brackets.find((one) => one.group === group);

    if (already) already.at.push(at);
    else brackets.push({ group, at: [at] });
  }

  /** A bracket number nothing is using, for starting a new one. */
  const spareGroup = (): number => {
    for (let number = 0; number < 16; number++) {
      if (!brackets.some((one) => one.group === number)) return number;
    }

    return 15;
  };

  /** AND or OR, as one small thing to press rather than a dropdown. */
  const Join = ({ at, about }: { at: number; about: string }): JSX.Element => (
    <button
      type="button"
      className={held[at]?.or ? 'editor-join or' : 'editor-join'}
      title={about}
      onClick={() => change(at, { or: held[at]?.or !== true })}
    >
      {held[at]?.or ? 'or' : 'and'}
    </button>
  );

  return (
    <>
      {brackets.map((bracket, which) => (
        <div className="editor-bracket" key={bracket.group}>
          {which > 0 && bracket.at[0] !== undefined ? (
            <Join at={bracket.at[0]} about="How this bracket joins the one above it" />
          ) : null}

          {bracket.at.map((at, inside) => {
            const condition = held[at];
            if (!condition) return null;

            return (
              <div className="editor-bracket-row" key={at}>
                {inside > 0 ? (
                  <Join at={at} about="How this condition joins the one above it" />
                ) : null}

                {renderCondition(condition, at)}
              </div>
            );
          })}

          {/* Another condition inside this bracket, rather than after all of
              them: a bracket is the thing being written, not the list. */}
          <button
            type="button"
            className="editor-tab quiet"
            onClick={() => onChange([...held, { flag: '', group: bracket.group }])}
          >
            Add Condition
          </button>
        </div>
      ))}

      <button
        type="button"
        className="editor-tab"
        onClick={() =>
          onChange([...held, { flag: '', group: held.length === 0 ? 0 : spareGroup() }])
        }
      >
        {held.length === 0 ? 'Add Condition' : 'Add Bracket'}
      </button>
    </>
  );

  function renderCondition(condition: Condition, at: number): JSX.Element {
    return (
      <div className="editor-condition">
        <select
          value={kindOfCondition(condition)}
          onChange={(event) =>
            onChange(
              held.map((entry, index) =>
                index === at
                  ? // Kept where it was written. Changing what a condition
                    // asks about is not moving it out of its bracket.
                    { ...blankCondition(event.target.value), or: entry.or, group: entry.group }
                  : entry,
              ),
            )
          }
        >
          <option value="flag">Flag</option>
          <option value="worldFlag">World Flag</option>
          <option value="atTime">Time Of Day</option>
          <option value="quest">Quest</option>
          <option value="skill">Skill</option>
          <option value="combat">Combat Level</option>
          <option value="item">Carrying</option>
          <option value="wearing">Wearing</option>
          <option value="belt">On Toolbelt</option>
          <option value="inPlace">In A Place</option>
          <option value="inArea">In Area</option>
          <option value="status">Under</option>
          <option value="praying">Praying</option>
          <option value="killed">Kills</option>
          <option value="deed">Has Done</option>
          <option value="standing">Standing Somewhere</option>
          <option value="chance">By Chance</option>
        </select>

        {condition.flag !== undefined ? (
          <>
            <Written
              key={`flag-${at}`}
              placeholder="flag"
              limit={48}
              value={condition.flag}
              onDone={(flag) => change(at, { flag })}
            />
            <Written
              key={`is-${at}`}
              placeholder="is (anything)"
              limit={48}
              value={condition.is ?? ''}
              onDone={(is) => change(at, { is })}
            />
          </>
        ) : null}

        {condition.atTime !== undefined ? (
          <select
            value={condition.atTime}
            onChange={(event) => change(at, { atTime: event.target.value })}
          >
            <option value="dawn">At dawn</option>
            <option value="day">In daylight</option>
            <option value="dusk">At dusk</option>
            <option value="night">At night</option>
          </select>
        ) : null}

        {condition.worldFlag !== undefined ? (
          <>
            <Written
              key={`world-${at}`}
              placeholder="world flag"
              limit={48}
              value={condition.worldFlag}
              onDone={(worldFlag) => change(at, { worldFlag })}
            />
            <Written
              key={`worldIs-${at}`}
              placeholder="is (anything)"
              limit={48}
              value={condition.worldIs ?? ''}
              onDone={(worldIs) => change(at, { worldIs })}
            />
          </>
        ) : null}

        {condition.quest !== undefined ? (
          <>
            <select
              value={condition.quest}
              onChange={(event) => change(at, { quest: event.target.value })}
            >
              <option value="">Choose a quest</option>
              {quests.map((quest) => (
                <option key={quest.id} value={quest.id}>
                  {quest.name}
                </option>
              ))}
            </select>

            {/* How far along, said in the words of the quest itself. It was
                  two unlabelled number boxes, one of them full of 999, which
                  asked somebody to know that a step is a number and that the
                  end of a quest is one past the last one. */}
            <select
              value={stageOf(condition, quests)}
              onChange={(event) => change(at, stageAt(event.target.value))}
            >
              <option value="any">Any Point</option>
              <option value="unstarted">Not Started</option>
              <option value="started">Started</option>
              <option value="finished">Finished</option>
              {(quests.find((quest) => quest.id === condition.quest)?.steps ?? []).map(
                (step, index) => (
                  <option key={index} value={`step:${index + 1}`}>
                    Step {index + 1}: {step.text.slice(0, 40)}
                  </option>
                ),
              )}
            </select>
          </>
        ) : null}

        {condition.skill !== undefined ? (
          <>
            <select
              value={condition.skill}
              onChange={(event) => change(at, { skill: event.target.value })}
            >
              {SKILLS.map((skill) => (
                <option key={skill} value={skill}>
                  {skill}
                </option>
              ))}
            </select>
            <label className="editor-number">
              <span>level</span>
              <input
                type="number"
                min={1}
                max={99}
                value={condition.level ?? 1}
                onChange={(event) => change(at, { level: Number(event.target.value) })}
              />
            </label>
          </>
        ) : null}

        {condition.combat !== undefined ? (
          <label className="editor-number">
            <span>at least</span>
            <input
              type="number"
              min={1}
              value={condition.combat}
              onChange={(event) => change(at, { combat: Number(event.target.value) })}
            />
          </label>
        ) : null}

        {condition.killed !== undefined ? (
          <>
            {/* By name where the names are known, since nobody writing a
                  conversation is holding a table of character ids. */}
            {characters && characters.length > 0 ? (
              <select
                value={condition.killed}
                onChange={(event) => change(at, { killed: Number(event.target.value) })}
              >
                <option value={0}>Choose a character</option>
                {[...characters]
                  .sort((a, b) => a.name.localeCompare(b.name))
                  .map((one) => (
                    <option key={one.id} value={one.id}>
                      {one.name}
                    </option>
                  ))}
              </select>
            ) : (
              <input
                type="number"
                min={0}
                title="Which kind of character, by id"
                value={condition.killed}
                onChange={(event) => change(at, { killed: Number(event.target.value) })}
              />
            )}
            <label className="editor-number">
              <span>how many</span>
              <input
                type="number"
                min={1}
                value={condition.atLeastKilled ?? 1}
                onChange={(event) => change(at, { atLeastKilled: Number(event.target.value) })}
              />
            </label>
          </>
        ) : null}

        {/* What they have ever done, which is neither what they carry nor
              where they are. The world keeps the tally itself, so nobody has to
              remember to write a flag and spell it the same way twice. */}
        {condition.deed !== undefined ? (
          <>
            <select
              value={condition.deed}
              onChange={(event) => change(at, { deed: event.target.value, deedOf: 0 })}
            >
              {DEEDS.map((entry) => (
                <option key={entry.deed} value={entry.deed}>
                  {entry.label}
                </option>
              ))}
            </select>

            {/* Firemaking is the one skill that leaves something standing, so
                  it is the one that asks about scenery rather than about what
                  came out of the work. */}
            {condition.deed === 'firemaking' || condition.deed === 'used' ? (
              <select
                value={condition.deedOf ?? 0}
                onChange={(event) => change(at, { deedOf: Number(event.target.value) })}
              >
                <option value={0}>Choose scenery</option>
                {objects.map((object) => (
                  <option key={object.id} value={object.id}>
                    {object.name}
                  </option>
                ))}
              </select>
            ) : (
              <select
                value={condition.deedOf ?? 0}
                onChange={(event) => change(at, { deedOf: Number(event.target.value) })}
              >
                <option value={0}>Choose an item</option>
                {items.map((item) => (
                  <option key={item.id} value={item.id}>
                    {item.name}
                  </option>
                ))}
              </select>
            )}

            <label className="editor-number">
              <span>at least</span>
              <input
                type="number"
                min={1}
                value={condition.atLeastDone ?? 1}
                onChange={(event) => change(at, { atLeastDone: Number(event.target.value) })}
              />
            </label>
          </>
        ) : null}

        {condition.item !== undefined ? (
          <>
            {/* With nothing chosen the box says so. Without the empty option
                  it showed a name while holding nought, and a condition on
                  nought is a condition that can never be true: a branch built
                  that way sends everybody down the False side forever. */}
            <select
              value={condition.item}
              onChange={(event) => change(at, { item: Number(event.target.value) })}
            >
              <option value={0}>Choose an item</option>
              {items.map((item) => (
                <option key={item.id} value={item.id}>
                  {item.name}
                </option>
              ))}
            </select>
            <label className="editor-number">
              <span>how many</span>
              <input
                type="number"
                min={1}
                value={condition.count ?? 1}
                onChange={(event) => change(at, { count: Number(event.target.value) })}
              />
            </label>
          </>
        ) : null}

        {condition.wearing !== undefined ? (
          <select
            value={condition.wearing}
            aria-label={condition.onBelt === true ? 'On the toolbelt' : 'Worn as gear'}
            onChange={(event) => change(at, { wearing: Number(event.target.value) })}
          >
            <option value={0}>Choose an item</option>
            {items.map((item) => (
              <option key={item.id} value={item.id}>
                {item.name}
              </option>
            ))}
          </select>
        ) : null}

        {/* Somewhere named, which is what anything referred to twice should
            use: a place that has been moved is still that place, and a
            rectangle written down twice is not. */}
        {condition.inPlace !== undefined ? (
          <label className="editor-number">
            <span>place</span>
            <input
              type="number"
              min={0}
              value={condition.inPlace}
              onChange={(event) =>
                change(at, { inPlace: Math.max(0, Math.round(Number(event.target.value)) || 0) })
              }
            />
            <small>The number the place carries</small>
          </label>
        ) : null}

        {/* Something that was put on them and has not worn off: warded,
            cursed, poisoned, whatever a spell leaves behind. */}
        {condition.status !== undefined ? (
          <Written
            key={`status-${at}`}
            placeholder="poisoned"
            limit={32}
            value={condition.status}
            onDone={(status) => change(at, { status })}
          />
        ) : null}

        {/* A prayer they are holding up at this moment, as against one they
            are able to use. */}
        {condition.praying !== undefined ? (
          <label className="editor-number">
            <span>prayer</span>
            <input
              type="number"
              min={0}
              value={condition.praying}
              onChange={(event) =>
                change(at, { praying: Math.max(0, Math.round(Number(event.target.value)) || 0) })
              }
            />
          </label>
        ) : null}

        {condition.inArea !== undefined ? (
          <>
            {(['tx', 'tz', 'width', 'height'] as const).map((field) => (
              <label className="editor-number" key={field}>
                <span>
                  {field === 'tx'
                    ? 'Left'
                    : field === 'tz'
                      ? 'Top'
                      : field === 'width'
                        ? 'Across'
                        : 'Down'}
                </span>
                <input
                  type="number"
                  value={condition.inArea?.[field] ?? 0}
                  onChange={(event) =>
                    change(at, {
                      inArea: {
                        ...(condition.inArea ?? { tx: 0, tz: 0, width: 1, height: 1, plane: 0 }),
                        [field]:
                          Math.round(Number(event.target.value)) ||
                          (field === 'width' || field === 'height' ? 1 : 0),
                      },
                    })
                  }
                />
              </label>
            ))}
          </>
        ) : null}

        {/* What is standing on a tile, so a lever can ask whether its gate is
            already open without a flag somebody has to keep in step. */}
        {condition.standing !== undefined ? (
          <>
            {(['tx', 'tz'] as const).map((field) => (
              <label className="editor-number" key={`standing-${field}`}>
                <span>{field === 'tx' ? 'Across' : 'Down'}</span>
                <input
                  type="number"
                  value={condition.standing?.[field] ?? 0}
                  onChange={(event) =>
                    change(at, {
                      standing: {
                        ...(condition.standing ?? NOTHING_THERE),
                        [field]: Math.round(Number(event.target.value)) || 0,
                      },
                    })
                  }
                />
              </label>
            ))}
            <button
              type="button"
              className="editor-tool"
              title="Click the thing in the world this asks about"
              onClick={() =>
                pickTile((picked) => {
                  if (picked) {
                    change(at, { standing: { ...(condition.standing ?? NOTHING_THERE), ...picked } });
                  }
                })
              }
            >
              Pick in the world
            </button>
            <select
              value={condition.standing.objectId}
              onChange={(event) =>
                change(at, {
                  standing: {
                    ...(condition.standing ?? NOTHING_THERE),
                    objectId: Number(event.target.value),
                  },
                })
              }
            >
              <option value={0}>Nothing standing there</option>
              {objects.map((one) => (
                <option key={one.id} value={one.id}>
                  {one.name}
                </option>
              ))}
            </select>
          </>
        ) : null}

        {/* The one question here that gives a different answer twice running. */}
        {condition.chance !== undefined ? (
          <label className="editor-number">
            <span>%</span>
            <input
              type="number"
              min={1}
              max={99}
              value={condition.chance}
              onChange={(event) =>
                change(at, { chance: Math.min(99, Math.max(1, Number(event.target.value) || 1)) })
              }
            />
          </label>
        ) : null}

        <button
          type="button"
          className={condition.not === true ? 'editor-tab on' : 'editor-tab'}
          title="Turn it round: true when this is not so"
          onClick={() => change(at, { not: condition.not !== true })}
        >
          not
        </button>

        <button
          type="button"
          className="editor-tab"
          onClick={() => onChange(held.filter((_, index) => index !== at))}
        >
          ✕
        </button>
      </div>
    );
  }
}

/** The list of things that happen. */
export function Effects({
  held,
  items,
  quests,
  shops,
  characters = [],
  objects = [],
  onChange,
}: {
  held: readonly Effect[];
  items: readonly ItemDefinitionWire[];
  quests: readonly QuestWire[];
  /** The shops there are, for an answer that opens one. */
  shops: readonly { id: number; name: string }[];
  /** Everybody there is, for one that walks into a scene. */
  characters?: readonly { id: number; name: string }[];
  /** The scenery there is, for the effects that turn one thing into another. */
  objects?: readonly { id: number; name: string }[];
  onChange: (effects: Effect[]) => void;
}): JSX.Element {
  const change = (at: number, patch: Effect): void => {
    onChange(held.map((entry, index) => (index === at ? { ...entry, ...patch } : entry)));
  };

  return (
    <>
      {held.map((effect, at) => (
        <div className="editor-condition" key={at}>
          <select
            value={kindOfEffect(effect)}
            onChange={(event) =>
              onChange(
                held.map((entry, index) =>
                  index === at ? blankEffect(event.target.value) : entry,
                ),
              )
            }
          >
            {/* Grouped by what it acts on. There are twenty five of these now
                and a flat list of twenty five is a list nobody reads to the
                bottom of. */}
            <optgroup label="Remember">
              <option value="setFlag">Set Flag</option>
              <option value="setsWorld">Set World Flag</option>
              <option value="quest">Set Quest Step</option>
            </optgroup>

            <optgroup label="Hand over">
              <option value="give">Give Item</option>
              <option value="take">Take Item</option>
              <option value="rolls">Give One Of</option>
              <option value="skill">Grant Experience</option>
            </optgroup>

            <optgroup label="Say">
              <option value="says">Message</option>
              <option value="thinks">Think</option>
              <option value="sound">Play Sound</option>
            </optgroup>

            <optgroup label="Open">
              <option value="opensShop">Open Shop</option>
              <option value="opensBank">Open Bank</option>
              <option value="opensExchange">Open Exchange</option>
              <option value="opensCrafting">Open Crafting</option>
              <option value="opensMenu">Open Menu</option>
            </optgroup>

            <optgroup label="The person">
              <option value="heals">Heal</option>
              <option value="hurts">Hurt</option>
              <option value="restores">Restore Energy</option>
              <option value="restoresMana">Restore Mana</option>
              <option value="restoresFaith">Restore Faith</option>
              <option value="plays">Play Animation</option>
              <option value="teleport">Teleport</option>
              <option value="travels">Travel</option>
            </optgroup>

            <optgroup label="The world">
              <option value="becomes">Turn Into</option>
              <option value="changes">Turn Something Elsewhere Into</option>
              <option value="gathers">Gather</option>
            </optgroup>

            <optgroup label="The scene">
              <option value="summon">Spawn Character</option>
              <option value="dismiss">Remove Character</option>
              <option value="holdCamera">Move Camera</option>
            </optgroup>
          </select>

          {/* A barber, a tailor, anything that is a window rather than a shop.
              Opening one ends the conversation, so the answer that does it has
              no way out of its own. */}
          {effect.opensMenu !== undefined ? (
            <>
              <select
                value={effect.opensMenu}
                onChange={(event) => change(at, { opensMenu: event.target.value })}
              >
                <option value="appearance">Appearance</option>
              </select>

              <label className="editor-number">
                <span>fee</span>
                <input
                  type="number"
                  min={0}
                  value={effect.fee ?? 0}
                  onChange={(event) => change(at, { fee: Number(event.target.value) })}
                />
              </label>
            </>
          ) : null}

          {/* What makes somebody a trader: an answer that opens their stock. */}
          {effect.opensShop !== undefined ? (
            <select
              value={effect.opensShop}
              onChange={(event) => change(at, { opensShop: Number(event.target.value) })}
            >
              <option value={0}>Choose a shop</option>
              {shops.map((shop) => (
                <option key={shop.id} value={shop.id}>
                  {shop.name}
                </option>
              ))}
            </select>
          ) : null}

          {effect.setFlag !== undefined ? (
            <>
              <Written
                key={`setflag-${at}`}
                placeholder="flag"
                limit={48}
                value={effect.setFlag}
                onDone={(setFlag) => change(at, { setFlag })}
              />
              <Written
                key={`value-${at}`}
                placeholder="yes"
                limit={48}
                value={effect.value ?? ''}
                onDone={(value) => change(at, { value })}
              />
            </>
          ) : null}

          {effect.setsWorld !== undefined ? (
            <>
              <Written
                key={`setsworld-${at}`}
                placeholder="world flag"
                limit={48}
                value={effect.setsWorld}
                onDone={(setsWorld) => change(at, { setsWorld })}
              />
              <Written
                key={`worldvalue-${at}`}
                placeholder="yes"
                limit={48}
                value={effect.value ?? ''}
                onDone={(value) => change(at, { value })}
              />
            </>
          ) : null}

          {effect.quest !== undefined ? (
            <>
              <select
                value={effect.quest}
                onChange={(event) => change(at, { quest: event.target.value })}
              >
                <option value="">Choose a quest</option>
                {quests.map((quest) => (
                  <option key={quest.id} value={quest.id}>
                    {quest.name}
                  </option>
                ))}
              </select>
              {/* Which step it moves the quest to, in the words of the quest.
                  It was a bare number, which asked somebody to keep the order
                  of twenty eight steps in their head while writing a line. */}
              <select
                value={String(effect.step ?? 1)}
                onChange={(event) => change(at, { step: Number(event.target.value) })}
              >
                <option value="1">Start it</option>
                {(quests.find((quest) => quest.id === effect.quest)?.steps ?? []).map(
                  (step, index) => (
                    <option key={index} value={index + 2}>
                      done {index + 1}: {step.text.slice(0, 40)}
                    </option>
                  ),
                )}
              </select>
            </>
          ) : null}

          {effect.give !== undefined || effect.take !== undefined ? (
            <>
              <select
                value={effect.give ?? effect.take ?? 0}
                onChange={(event) =>
                  change(
                    at,
                    effect.give !== undefined
                      ? { give: Number(event.target.value) }
                      : { take: Number(event.target.value) },
                  )
                }
              >
                <option value={0}>Choose an item</option>
                {items.map((item) => (
                  <option key={item.id} value={item.id}>
                    {item.name}
                  </option>
                ))}
              </select>
              <label className="editor-number">
                <span>how many</span>
                <input
                  type="number"
                  min={1}
                  value={effect.count ?? 1}
                  onChange={(event) => change(at, { count: Number(event.target.value) })}
                />
              </label>

              {/* Only for a hand-over. Taking something away twice is somebody
                  losing two of them, which is a different question. */}
              {effect.give !== undefined ? (
                <button
                  type="button"
                  className={effect.once === true ? 'editor-tab on' : 'editor-tab'}
                  title="Hand it over once and never again, whoever walks back into this line"
                  onClick={() => change(at, { once: effect.once !== true })}
                >
                  once
                </button>
              ) : null}
            </>
          ) : null}

          {effect.skill !== undefined ? (
            <>
              <select
                value={effect.skill}
                onChange={(event) => change(at, { skill: event.target.value })}
              >
                {SKILLS.map((skill) => (
                  <option key={skill} value={skill}>
                    {skill}
                  </option>
                ))}
              </select>
              <input
                type="number"
                value={effect.experience ?? 0}
                onChange={(event) => change(at, { experience: Number(event.target.value) })}
              />
            </>
          ) : null}

          {effect.summon !== undefined ? (
            <>
              <select
                value={effect.summon.npcId}
                onChange={(event) =>
                  change(at, {
                    summon: { ...effect.summon!, npcId: Number(event.target.value) },
                  })
                }
              >
                <option value={0}>Choose somebody</option>
                {characters.map((one) => (
                  <option key={one.id} value={one.id}>
                    {one.name}
                  </option>
                ))}
              </select>

              {(
                [
                  ['tx', 'In at'],
                  ['tz', ''],
                  ['walkTx', 'Walks to'],
                  ['walkTz', ''],
                ] as const
              ).map(([field, label]) => (
                <label className="editor-number" key={field}>
                  <span>{label}</span>
                  <input
                    type="number"
                    value={effect.summon?.[field] ?? 0}
                    onChange={(event) =>
                      change(at, {
                        summon: {
                          ...effect.summon!,
                          [field]: Math.round(Number(event.target.value)) || 0,
                        },
                      })
                    }
                  />
                </label>
              ))}

              <label className="editor-number">
                <span>Stays</span>
                <input
                  type="number"
                  value={effect.summon.forTicks}
                  onChange={(event) =>
                    change(at, {
                      summon: {
                        ...effect.summon!,
                        forTicks: Math.max(0, Math.round(Number(event.target.value)) || 0),
                      },
                    })
                  }
                />
              </label>
            </>
          ) : null}

          {effect.holdCamera !== undefined ? (
            <>
              {(
                [
                  ['tx', 'On'],
                  ['tz', ''],
                  ['tenths', 'Tenths'],
                ] as const
              ).map(([field, label]) => (
                <label className="editor-number" key={field}>
                  <span>{label}</span>
                  <input
                    type="number"
                    value={effect.holdCamera?.[field] ?? 0}
                    onChange={(event) =>
                      change(at, {
                        holdCamera: {
                          ...effect.holdCamera!,
                          [field]: Math.round(Number(event.target.value)) || 0,
                        },
                      })
                    }
                  />
                </label>
              ))}
            </>
          ) : null}

          {effect.give !== undefined ? (
            <>
              <span className="tool-label">If full</span>
              <select
                value={effect.whenFull ?? 'held'}
                onChange={(event) =>
                  change(at, { whenFull: event.target.value as Effect['whenFull'] })
                }
              >
                <option value="held">Hold it back</option>
                <option value="bank">Send it to the bank</option>
                <option value="drop">Drop it at their feet</option>
              </select>
            </>
          ) : null}

          {effect.teleport !== undefined ? (
            <>
              {(['tx', 'tz'] as const).map((field) => (
                <label className="editor-number" key={field}>
                  <span>{field === 'tx' ? 'Across' : 'Down'}</span>
                  <input
                    type="number"
                    value={effect.teleport?.[field] ?? 0}
                    onChange={(event) =>
                      change(at, {
                        teleport: {
                          ...(effect.teleport ?? { tx: 0, tz: 0, plane: 0 }),
                          [field]: Math.round(Number(event.target.value)) || 0,
                        },
                      })
                    }
                  />
                </label>
              ))}
              {/* Where it lands, chosen by looking at the place rather than by
                  typing a number that turns out to be inside a wall. */}
              <button
                type="button"
                className="editor-tool"
                title="Click the spot in the world where it should land"
                onClick={() =>
                  pickTile((picked) => {
                    if (picked) change(at, { teleport: picked });
                  })
                }
              >
                Pick in the world
              </button>
              <button
                type="button"
                className="editor-tool"
                title="Send the camera to where it lands"
                onClick={() => lookAtTile(effect.teleport?.tx ?? 0, effect.teleport?.tz ?? 0)}
              >
                Look there
              </button>
            </>
          ) : null}

          {effect.says !== undefined ? (
            <Written
              key={`says-${at}`}
              value={effect.says}
              limit={160}
              placeholder="What the world says back"
              onDone={(says) => change(at, { says })}
            />
          ) : null}

          {effect.rolls !== undefined ? (
            <RollFields
              held={effect.rolls}
              items={items}
              onChange={(rolls) => change(at, { rolls })}
            />
          ) : null}

          {effect.sound !== undefined ? (
            <SoundPicker
              label="Sound"
              value={effect.sound}
              empty="Choose a sound"
              onChange={(sound) => change(at, { sound })}
            />
          ) : null}

          {effect.restoresMana !== undefined ? (
            <label className="editor-small">
              <span>Mana</span>
              <input
                type="number"
                min={1}
                max={10000}
                value={effect.restoresMana}
                onChange={(event) =>
                  change(at, {
                    restoresMana: Math.max(1, Math.round(Number(event.target.value) || 1)),
                  })
                }
              />
            </label>
          ) : null}

          {effect.restoresFaith !== undefined ? (
            <label className="editor-small">
              <span>Faith</span>
              <input
                type="number"
                min={1}
                max={10000}
                value={effect.restoresFaith}
                onChange={(event) =>
                  change(at, {
                    restoresFaith: Math.max(1, Math.round(Number(event.target.value) || 1)),
                  })
                }
              />
            </label>
          ) : null}

          {/* A line in their own voice rather than the world's. */}
          {effect.thinks !== undefined ? (
            <Written
              key={`thinks-${at}`}
              value={effect.thinks}
              limit={160}
              placeholder="What they think to themselves"
              onDone={(thinks) => change(at, { thinks })}
            />
          ) : null}

          {effect.heals !== undefined ? (
            <label className="editor-small">
              <span>Hit points</span>
              <input
                type="number"
                min={1}
                max={1000}
                value={effect.heals}
                onChange={(event) => change(at, { heals: Number(event.target.value) })}
              />
            </label>
          ) : null}

          {effect.restores !== undefined ? (
            <label className="editor-small">
              <span>Energy</span>
              <input
                type="number"
                min={1}
                max={10000}
                step={500}
                value={effect.restores}
                onChange={(event) => change(at, { restores: Number(event.target.value) })}
              />
              <small>Ten thousand is a full run</small>
            </label>
          ) : null}

          {effect.plays !== undefined ? (
            <select
              value={effect.plays}
              onChange={(event) => change(at, { plays: event.target.value as 'bend' | 'pickup' })}
            >
              <option value="bend">Bending over it</option>
              <option value="pickup">Stooping</option>
            </select>
          ) : null}

          {/* What stands here instead, and for how long. A lever thrown, a
              chest opened, a rock mined out. */}
          {effect.becomes !== undefined ? (
            <>
              <select
                value={effect.becomes.objectId}
                onChange={(event) =>
                  change(at, {
                    becomes: {
                      objectId: Number(event.target.value),
                      forTicks: effect.becomes?.forTicks ?? 100,
                    },
                  })
                }
              >
                <option value={0}>Choose what it turns into</option>
                {objects.map((one) => (
                  <option key={one.id} value={one.id}>
                    {one.name}
                  </option>
                ))}
              </select>
              <label className="editor-small">
                <span>For</span>
                <input
                  type="number"
                  min={0}
                  max={36000}
                  value={effect.becomes.forTicks}
                  onChange={(event) =>
                    change(at, {
                      becomes: {
                        objectId: effect.becomes?.objectId ?? 0,
                        forTicks: Number(event.target.value),
                      },
                    })
                  }
                />
                <small>Ticks. Nought leaves it as it is</small>
              </label>
              <ForWhom
                held={effect.becomes.forWhom}
                onChange={(forWhom) =>
                  change(at, { becomes: { ...(effect.becomes ?? BECOMES), forWhom } })
                }
              />
            </>
          ) : null}

          {/* The gate at the other end of the room. Whatever is standing on the
              tile turns into this; nothing chosen takes it away instead. */}
          {effect.changes !== undefined ? (
            <>
              {(['tx', 'tz'] as const).map((field) => (
                <label className="editor-number" key={`changes-${field}`}>
                  <span>{field === 'tx' ? 'Across' : 'Down'}</span>
                  <input
                    type="number"
                    value={effect.changes?.[field] ?? 0}
                    onChange={(event) =>
                      change(at, {
                        changes: {
                          ...(effect.changes ?? NOWHERE),
                          [field]: Math.round(Number(event.target.value)) || 0,
                        },
                      })
                    }
                  />
                </label>
              ))}
              {/* Chosen by looking at it, because a gate is somewhere somebody
                  can see and a pair of numbers is not. */}
              <button
                type="button"
                className="editor-tool"
                title="Click the thing in the world this should change"
                onClick={() =>
                  pickTile((picked) => {
                    if (picked) {
                      change(at, { changes: { ...(effect.changes ?? NOWHERE), ...picked } });
                    }
                  })
                }
              >
                Pick in the world
              </button>
              <button
                type="button"
                className="editor-tool"
                title="Send the camera to what it changes"
                onClick={() => lookAtTile(effect.changes?.tx ?? 0, effect.changes?.tz ?? 0)}
              >
                Look there
              </button>
              <select
                value={effect.changes.objectId}
                onChange={(event) =>
                  change(at, {
                    changes: {
                      ...(effect.changes ?? NOWHERE),
                      objectId: Number(event.target.value),
                    },
                  })
                }
              >
                <option value={0}>Take it away</option>
                {objects.map((one) => (
                  <option key={one.id} value={one.id}>
                    {one.name}
                  </option>
                ))}
              </select>
              {/* Only when it turns into something. Taking a thing away has
                  nothing to turn back into, so offering a time here would
                  promise something that never happens. */}
              <label className="editor-small" hidden={effect.changes.objectId === 0}>
                <span>For</span>
                <input
                  type="number"
                  min={0}
                  max={36000}
                  value={effect.changes.forTicks}
                  onChange={(event) =>
                    change(at, {
                      changes: {
                        ...(effect.changes ?? NOWHERE),
                        forTicks: Number(event.target.value),
                      },
                    })
                  }
                />
                <small>Ticks. Nought leaves it as it is</small>
              </label>
              <ForWhom
                held={effect.changes.forWhom}
                onChange={(forWhom) => change(at, { changes: { ...(effect.changes ?? NOWHERE), forWhom } })}
              />
            </>
          ) : null}

          {/* A trap, a brazier, a thorn hedge. It stops at one health unless it
              is told it may take the last of it. */}
          {effect.hurts !== undefined ? (
            <>
              <label className="editor-small">
                <span>Takes</span>
                <input
                  type="number"
                  min={1}
                  max={1000}
                  value={effect.hurts.amount}
                  onChange={(event) =>
                    change(at, {
                      hurts: {
                        amount: Number(event.target.value),
                        canKill: effect.hurts?.canKill ?? false,
                      },
                    })
                  }
                />
              </label>
              <button
                type="button"
                className={effect.hurts.canKill === true ? 'editor-tab on' : 'editor-tab'}
                title="Whether this one may take the last of somebody's health"
                onClick={() =>
                  change(at, {
                    hurts: {
                      amount: effect.hurts?.amount ?? 5,
                      canKill: effect.hurts?.canKill !== true,
                    },
                  })
                }
              >
                May kill
              </button>
            </>
          ) : null}

          {effect.opensCrafting !== undefined ? (
            <select
              value={effect.opensCrafting}
              onChange={(event) => change(at, { opensCrafting: Number(event.target.value) })}
            >
              <option value={0}>Choose whose recipes to offer</option>
              {objects.map((one) => (
                <option key={one.id} value={one.id}>
                  {one.name}
                </option>
              ))}
            </select>
          ) : null}

          {effect.travels !== undefined ? (
            <>
              <input
                type="text"
                value={effect.travels.region}
                placeholder="Region, or empty to come back out"
                onChange={(event) =>
                  change(at, {
                    travels: {
                      region: event.target.value,
                      instanced: effect.travels?.instanced ?? true,
                    },
                  })
                }
              />
              <button
                type="button"
                className={effect.travels.instanced ? 'editor-tab on' : 'editor-tab'}
                title={
                  effect.travels.instanced
                    ? 'A copy of their own, which nobody else is in'
                    : 'The one everybody shares'
                }
                onClick={() =>
                  change(at, {
                    travels: {
                      region: effect.travels?.region ?? '',
                      instanced: !(effect.travels?.instanced ?? true),
                    },
                  })
                }
              >
                {effect.travels.instanced ? 'Their own copy' : 'Shared'}
              </button>
            </>
          ) : null}

          {/* Work at a place. What it gives is a list of things, because one
              tree is a pine and the next is an oak by what they hold. */}
          {effect.gathers !== undefined ? (
            <GatherForm
              held={effect.gathers}
              items={items}
              objects={objects}
              onChange={(gathers) => change(at, { gathers })}
            />
          ) : null}

          <button
            type="button"
            className="editor-tab"
            onClick={() => onChange(held.filter((_, index) => index !== at))}
          >
            ✕
          </button>
        </div>
      ))}

      <button
        type="button"
        className="editor-tab"
        onClick={() => onChange([...held, { says: '' }])}
      >
        Add Effect
      </button>
    </>
  );
}

function kindOfCondition(condition: Condition): string {
  if (condition.atTime !== undefined) return 'atTime';
  if (condition.worldFlag !== undefined) return 'worldFlag';
  if (condition.wearing !== undefined) return condition.onBelt === true ? 'belt' : 'wearing';
  if (condition.inArea !== undefined) return 'inArea';
  if (condition.inPlace !== undefined) return 'inPlace';
  if (condition.status !== undefined) return 'status';
  if (condition.praying !== undefined) return 'praying';
  if (condition.quest !== undefined) return 'quest';
  if (condition.skill !== undefined) return 'skill';
  if (condition.combat !== undefined) return 'combat';
  if (condition.killed !== undefined) return 'killed';
  if (condition.deed !== undefined) return 'deed';
  if (condition.item !== undefined) return 'item';
  if (condition.standing !== undefined) return 'standing';
  if (condition.chance !== undefined) return 'chance';
  return 'flag';
}

function blankCondition(kind: string): Condition {
  switch (kind) {
    case 'atTime':
      return { atTime: 'night' };
    case 'worldFlag':
      return { worldFlag: '', worldIs: '' };
    case 'quest':
      return { quest: '', atLeast: 1 };
    case 'skill':
      return { skill: 'woodcutting', level: 1 };
    case 'combat':
      return { combat: 3 };
    case 'item':
      return { item: 0, count: 1 };
    case 'killed':
      return { killed: 0, atLeastKilled: 1 };
    case 'deed':
      return { deed: 'cooking', deedOf: 0, atLeastDone: 1 };
    case 'wearing':
      return { wearing: 0 };
    case 'belt':
      return { wearing: 0, onBelt: true };
    case 'inArea':
      return { inArea: { tx: 0, tz: 0, width: 1, height: 1, plane: 0 } };
    case 'inPlace':
      return { inPlace: 0 };
    case 'status':
      return { status: '' };
    case 'praying':
      return { praying: 0 };
    case 'standing':
      return { standing: { tx: 0, tz: 0, plane: 0, objectId: 0 } };
    case 'chance':
      return { chance: 50 };
    default:
      return { flag: '' };
  }
}

/**
 * Work at a place, written out.
 *
 * More than a row of boxes because gathering is more than one number: which
 * skill, what it takes, how often it comes off, what comes up, and what is left
 * standing afterwards. Kept beside the other effects rather than in a panel of
 * its own, because it is one of them.
 */
function GatherForm({
  held,
  items,
  objects,
  onChange,
}: {
  held: NonNullable<Effect['gathers']>;
  items: readonly ItemDefinitionWire[];
  objects: readonly { id: number; name: string }[];
  onChange: (gathers: NonNullable<Effect['gathers']>) => void;
}): JSX.Element {
  const set = (patch: Partial<NonNullable<Effect['gathers']>>): void =>
    onChange({ ...held, ...patch });

  return (
    <div className="editor-gather">
      <label className="editor-small">
        <span>Skill</span>
        <select value={held.skill} onChange={(event) => set({ skill: event.target.value })}>
          {SKILLS.map((skill) => (
            <option key={skill} value={skill}>
              {skill}
            </option>
          ))}
        </select>
      </label>

      {/* What comes up. A list, because one tree is a pine and the next an oak
          by what they hold rather than by being two kinds of tree. What each
          takes and gives is read off the thing itself, so it is not asked
          twice. */}
      <span className="tool-label">What it gives</span>
      {held.catches.map((entry, at) => (
        <div className="editor-brushes" key={at}>
          <select
            value={entry.itemId}
            onChange={(event) =>
              set({
                catches: held.catches.map((one, index) =>
                  index === at ? { ...one, itemId: Number(event.target.value) } : one,
                ),
              })
            }
          >
            <option value={0}>Choose something</option>
            {items.map((item) => (
              <option key={item.id} value={item.id}>
                {item.name}
              </option>
            ))}
          </select>
          <input
            type="number"
            min={1}
            max={100}
            title="How often this one comes up against the others"
            value={entry.weight}
            onChange={(event) =>
              set({
                catches: held.catches.map((one, index) =>
                  index === at ? { ...one, weight: Number(event.target.value) } : one,
                ),
              })
            }
          />
          <button
            type="button"
            className="editor-tab danger"
            onClick={() => set({ catches: held.catches.filter((_, index) => index !== at) })}
          >
            ✕
          </button>
        </div>
      ))}

      <button
        type="button"
        className="editor-tab"
        onClick={() =>
          set({
            catches: [...held.catches, { itemId: 0, weight: 1, level: 1, experience: 0 }],
          })
        }
      >
        Add Something It Gives
      </button>

      <label className="editor-small">
        <span>Needs</span>
        <select
          value={held.tools[0] ?? 0}
          onChange={(event) =>
            set({ tools: Number(event.target.value) > 0 ? [Number(event.target.value)] : [] })
          }
        >
          <option value={0}>nothing in hand</option>
          {items.map((item) => (
            <option key={item.id} value={item.id}>
              {item.name}
            </option>
          ))}
        </select>
      </label>

      <label className="editor-small">
        <span>Every</span>
        <input
          type="number"
          min={1}
          max={20}
          value={held.interval}
          onChange={(event) => set({ interval: Number(event.target.value) })}
        />
        <small>Ticks between tries</small>
      </label>

      <label className="editor-small">
        <span>Odds</span>
        <input
          type="number"
          min={1}
          max={100}
          value={Math.round(held.chance * 100)}
          onChange={(event) => set({ chance: Math.max(1, Number(event.target.value)) / 100 })}
        />
        <small>Per cent at the level it takes</small>
      </label>

      {/* What is left standing once it has given something up. A cut pine and a
          cut oak are both a stump, so this is one shared thing rather than a
          second definition for every tier. */}
      <label className="editor-small">
        <span>Leaves</span>
        <select
          value={held.depletesTo}
          onChange={(event) => set({ depletesTo: Number(event.target.value) })}
        >
          <option value={0}>nothing, it stays as it is</option>
          {objects.map((one) => (
            <option key={one.id} value={one.id}>
              {one.name}
            </option>
          ))}
        </select>
      </label>

      {held.depletesTo > 0 ? (
        <label className="editor-small">
          <span>Comes back after</span>
          <input
            type="number"
            min={1}
            max={36000}
            value={held.depleteTicks}
            onChange={(event) => set({ depleteTicks: Number(event.target.value) })}
          />
          <small>Ticks</small>
        </label>
      ) : null}
    </div>
  );
}

/** A tile nobody has chosen yet, so a half filled effect still has a shape. */
const NOWHERE = { tx: 0, tz: 0, plane: 0, objectId: 0, forTicks: 0 };

/** The same, for the one that acts on the thing being pressed. */
const BECOMES = { objectId: 0, forTicks: 0 };

/**
 * Who a change to the world happens for.
 *
 * The world is the usual answer. The other is the one a quest needs: a gate
 * that opens because somebody finished a quest should be open for them and shut
 * for everybody who has not, and still open when they come back next week.
 */
function ForWhom({
  held,
  onChange,
}: {
  held: 'world' | 'me' | undefined;
  onChange: (forWhom: 'world' | 'me') => void;
}): JSX.Element {
  return (
    <button
      type="button"
      className={held === 'me' ? 'editor-tab on' : 'editor-tab'}
      title={
        held === 'me'
          ? 'For whoever did it, and nobody else. Kept, so it is still so next week'
          : 'For the world: the next person along finds it as it was left'
      }
      onClick={() => onChange(held === 'me' ? 'world' : 'me')}
    >
      {held === 'me' ? 'for them alone' : 'for the world'}
    </button>
  );
}

/** The same, for a condition, which asks about a tile but does not change it. */
const NOTHING_THERE = { tx: 0, tz: 0, plane: 0, objectId: 0 };

function kindOfEffect(effect: Effect): string {
  if (effect.gathers !== undefined) return 'gathers';
  if (effect.becomes !== undefined) return 'becomes';
  if (effect.changes !== undefined) return 'changes';
  if (effect.hurts !== undefined) return 'hurts';
  if (effect.travels !== undefined) return 'travels';
  if (effect.opensCrafting !== undefined) return 'opensCrafting';
  if (effect.opensBank !== undefined) return 'opensBank';
  if (effect.opensExchange !== undefined) return 'opensExchange';
  if (effect.rolls !== undefined) return 'rolls';
  if (effect.heals !== undefined) return 'heals';
  if (effect.restores !== undefined) return 'restores';
  if (effect.restoresMana !== undefined) return 'restoresMana';
  if (effect.restoresFaith !== undefined) return 'restoresFaith';
  if (effect.sound !== undefined) return 'sound';
  if (effect.thinks !== undefined) return 'thinks';
  if (effect.plays !== undefined) return 'plays';
  if (effect.opensMenu !== undefined) return 'opensMenu';
  if (effect.summon !== undefined) return 'summon';
  if (effect.dismiss !== undefined) return 'dismiss';
  if (effect.holdCamera !== undefined) return 'holdCamera';
  if (effect.teleport !== undefined) return 'teleport';
  if (effect.opensShop !== undefined) return 'opensShop';
  if (effect.quest !== undefined) return 'quest';
  if (effect.give !== undefined) return 'give';
  if (effect.take !== undefined) return 'take';
  if (effect.skill !== undefined) return 'skill';
  if (effect.setsWorld !== undefined) return 'setsWorld';
  if (effect.setFlag !== undefined) return 'setFlag';
  return 'says';
}

function blankEffect(kind: string): Effect {
  switch (kind) {
    case 'setsWorld':
      return { setsWorld: '', value: 'yes' };
    case 'heals':
      return { heals: 10 };
    case 'restores':
      return { restores: 2000 };
    case 'restoresMana':
      return { restoresMana: 10 };
    case 'restoresFaith':
      return { restoresFaith: 10 };
    case 'sound':
      return { sound: '' };
    case 'thinks':
      return { thinks: '' };
    case 'plays':
      return { plays: 'bend' };
    case 'becomes':
      return { becomes: { objectId: 0, forTicks: 100 } };
    case 'changes':
      return { changes: { tx: 0, tz: 0, plane: 0, objectId: 0, forTicks: 0 } };
    case 'hurts':
      return { hurts: { amount: 5, canKill: false } };
    case 'opensBank':
      return { opensBank: true };
    case 'opensExchange':
      return { opensExchange: true };
    case 'rolls':
      return { rolls: { entries: [{ itemId: 0, min: 1, max: 1, weight: 1 }], times: 1 } };
    case 'opensCrafting':
      return { opensCrafting: 0 };
    case 'travels':
      return { travels: { region: '', instanced: true } };
    case 'gathers':
      return {
        gathers: {
          skill: 'woodcutting',
          level: 1,
          experience: 25,
          itemId: 0,
          catches: [],
          animation: 'bend',
          tools: [],
          interval: 4,
          chance: 0.3,
          chancePerLevel: 0.01,
          depletesTo: 0,
          depleteTicks: 100,
        },
      };
    case 'quest':
      return { quest: '', step: 1 };
    case 'give':
      return { give: 0, count: 1 };
    case 'take':
      return { take: 0, count: 1 };
    case 'skill':
      return { skill: 'woodcutting', experience: 50 };
    case 'setFlag':
      return { setFlag: '', value: 'yes' };
    case 'opensMenu':
      return { opensMenu: 'appearance', fee: 0 };
    case 'opensShop':
      return { opensShop: 0 };
    case 'teleport':
      return { teleport: { tx: 0, tz: 0, plane: 0 } };
    case 'summon':
      return { summon: { npcId: 0, tx: 0, tz: 0, plane: 0, forTicks: 200 } };
    case 'dismiss':
      return { dismiss: true };
    case 'holdCamera':
      return { holdCamera: { tx: 0, tz: 0, tenths: 30 } };
    default:
      return { says: '' };
  }
}


/**
 * One of several things, by weight.
 *
 * The same shape a drop table has, written here because the thing handing it
 * over is usually a casket or a crate rather than something that was killed.
 * A weight against nothing at all is what makes the rest of it rare.
 */
function RollFields({
  held,
  items,
  onChange,
}: {
  held: NonNullable<Effect['rolls']>;
  items: readonly ItemDefinitionWire[];
  onChange: (rolls: NonNullable<Effect['rolls']>) => void;
}): JSX.Element {
  const total = held.entries.reduce((sum, one) => sum + Math.max(1, one.weight), 0);
  const set = (at: number, patch: Partial<(typeof held.entries)[number]>): void =>
    onChange({
      ...held,
      entries: held.entries.map((one, index) => (index === at ? { ...one, ...patch } : one)),
    });

  return (
    <div className="magic-box">
      {held.entries.map((one, at) => (
        <div className="editor-brushes" key={at}>
          <ItemPicker
            items={items}
            value={one.itemId}
            allowNothing
            nothingLabel="nothing"
            onPick={(itemId) => set(at, { itemId })}
          />
          <label className="editor-small">
            <span>least</span>
            <input
              type="number"
              min={1}
              value={one.min}
              onChange={(event) => set(at, { min: Math.max(1, Number(event.target.value) || 1) })}
            />
          </label>
          <label className="editor-small">
            <span>most</span>
            <input
              type="number"
              min={1}
              value={one.max}
              onChange={(event) => set(at, { max: Math.max(1, Number(event.target.value) || 1) })}
            />
          </label>
          <label className="editor-small">
            <span>weight</span>
            <input
              type="number"
              min={1}
              value={one.weight}
              onChange={(event) =>
                set(at, { weight: Math.max(1, Number(event.target.value) || 1) })
              }
            />
          </label>
          <span className="hint">{Math.round((Math.max(1, one.weight) / total) * 100)}%</span>
          <button
            type="button"
            className="editor-tool"
            onClick={() =>
              onChange({ ...held, entries: held.entries.filter((_, index) => index !== at) })
            }
          >
            ✕
          </button>
        </div>
      ))}
      <div className="editor-brushes">
        <button
          type="button"
          className="editor-tab"
          onClick={() =>
            onChange({
              ...held,
              entries: [...held.entries, { itemId: 0, min: 1, max: 1, weight: 1 }],
            })
          }
        >
          Add A Line
        </button>
        <label className="editor-small">
          <span>turns</span>
          <input
            type="number"
            min={1}
            max={10}
            value={held.times}
            onChange={(event) =>
              onChange({
                ...held,
                times: Math.min(10, Math.max(1, Number(event.target.value) || 1)),
              })
            }
          />
        </label>
      </div>

      {/* Whether there is anything left in it afterwards. Off is a thing that
          gives something every time, which is a slot machine rather than a
          chest, and is what everything written before this meant. */}
      <div className="editor-brushes">
        <button
          type="button"
          className={held.emptyFor !== undefined ? 'editor-tab on' : 'editor-tab'}
          title="Whether it is emptied by being looted"
          onClick={() =>
            onChange({ ...held, emptyFor: held.emptyFor === undefined ? 0 : undefined })
          }
        >
          empties
        </button>

        {held.emptyFor !== undefined ? (
          <>
            <label className="editor-small">
              <span>fills after</span>
              <input
                type="number"
                min={0}
                max={360000}
                value={held.emptyFor}
                onChange={(event) =>
                  onChange({
                    ...held,
                    emptyFor: Math.max(0, Math.round(Number(event.target.value) || 0)),
                  })
                }
              />
              <small>Ticks. Nought is emptied for good</small>
            </label>

            <button
              type="button"
              className={held.each === false ? 'editor-tab on' : 'editor-tab'}
              title={
                held.each === false
                  ? 'One hoard for the world: the first one there empties it for everybody'
                  : 'One for each person: four people finishing a quest find four chests with something in them'
              }
              onClick={() => onChange({ ...held, each: held.each === false })}
            >
              {held.each === false ? 'shared' : 'each person'}
            </button>
          </>
        ) : null}
      </div>

      {held.emptyFor !== undefined ? (
        <Written
          key="emptySays"
          placeholder="There is nothing left."
          limit={160}
          value={held.emptySays ?? ''}
          onDone={(emptySays) => onChange({ ...held, emptySays })}
        />
      ) : null}
    </div>
  );
}
